import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Loader } from "../../shared";
import { useMessageApi } from "../../shared/providers/MessageProvider";
import { BLOCKED_USER_FLAG } from "../../utils/constants";
import { isBlockedUserError } from "../../utils/auth0Utils";

/**
 * Callback Component
 * ------------------
 * Handles the authentication callback from Auth0.
 * The Auth0Provider automatically handles the callback, so this component
 * just waits for authentication to complete and redirects to the dashboard.
 * Also handles blocked user errors and displays appropriate error messages.
 */
const Callback = () => {
  // Get the authentication status from Auth0
  const { isLoading, isAuthenticated, error } = useAuth0();
  const navigate = useNavigate();
  const message = useMessageApi();

  // Use effect to handle the authentication process
  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
    const returnTo = new URL(window.location).pathname;
    navigate(returnTo, { replace: true });
    } else if (error) {
      // Check if this is a blocked user error
      if (isBlockedUserError(error)) {
        // Store flag in localStorage - message will be shown by showAuthStatusMessages
        localStorage.setItem(BLOCKED_USER_FLAG, 'true');
      } else {
        // Check for other Auth0 errors and show appropriate message
        const errorMessage = error?.error_description || error?.description || error?.message || "Login failed. Please try again.";
        message.error(`Login failed: ${errorMessage}`, 5);
      }
      navigate("/", { replace: true });
    }
  }, [isLoading, isAuthenticated, error, navigate, message]);

  // Return the loader component
  return <Loader isLoading={isLoading} text="Authenticating..." />;
};

export default Callback;
